import React, { useEffect, useMemo, useState } from 'react'
import styles from './styles.module.css'
import Button from '../../components/Button'
import { IoIosAdd, IoIosRefresh } from 'react-icons/io'
import { useDispatch, useSelector } from 'react-redux'
import Modal from './Modal'
import { BiTrash } from 'react-icons/bi'
import Confirmation from '../../components/Confirmation/Confirmation'
import { deleteSubgroupNew, getSubgroupCoursesList, getSubgroupList, getSubgroupTablesList } from '../../actions/directoryBasicEducation'
import { Checkbox } from '../../components/ModalInputs'
import CustomSelect from '../../components/CustomSelect'
import { useFormik } from 'formik'
import { arrayForSelect } from '../../utils/arrayForSelect'
import * as Yup from 'yup'
import { getSchoolYears } from '../../actions/common'
import { getCurrentYearUtils } from '../../utils/classBook'

export default function Subgroups() {
  const dispatch = useDispatch()
  const { tableList, subgroupClasses, subgroupCourses } = useSelector(state => state.directoryBasicEducation)
  const { schoolYears } = useSelector(state => state.common);

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(false);
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)
  const [electiveCourse, setElectiveCourse] = useState(false);
  const [statusClose, setStatusClose] = useState(false);

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }

  const sortedList = useMemo(() => {
    if (!tableList) return []
    return [...tableList].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [tableList, sortField, sortAsc])

  const validationSchema = useMemo(() => Yup.object().shape({
    xp_key: Yup.number().required('обязательное поле'),
    cid: Yup.number().required('обязательное поле'),
    ...(electiveCourse ? {} : { gid: Yup.number().required('обязательное поле') }),
  }), [electiveCourse])

  const buildRequestParams = (formValues) => ({
    school_year_id: formValues.xp_key,
    cid: formValues.cid,
    is_elective: electiveCourse,
    show_closed: statusClose,
    ...(!electiveCourse && formValues.gid ? { gid: formValues.gid } : {})
  })

  const handleUpdateTable = (formValues) => {
    dispatch(getSubgroupTablesList(buildRequestParams(formValues)))
  }

  const {
    values,
    setFieldValue,
    errors,
    touched,
    submitCount,
    handleSubmit
  } = useFormik({
    initialValues: { xp_key: '', cid: '', gid: '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleUpdateTable
  })

  useEffect(() => {
    dispatch(getSchoolYears())
  }, [dispatch])

  useEffect(() => {
    if (schoolYears?.length && !values.xp_key) {
      setFieldValue('xp_key', getCurrentYearUtils(schoolYears))
    }
  }, [schoolYears])

  useEffect(() => {
    values.xp_key && dispatch(getSubgroupCoursesList({ school_year_id: values.xp_key, show_closed: statusClose }))
  }, [values.xp_key, statusClose])

  useEffect(() => {
    values.xp_key && values.cid && dispatch(getSubgroupList({ school_year_id: values.xp_key, cid: values.cid }))
  }, [values.xp_key, values.cid])

  // автовыбор первого значения в каскадных фильтрах
  useEffect(() => {
    if (subgroupCourses?.length && !values.cid) setFieldValue('cid', subgroupCourses[0].cid)
  }, [subgroupCourses])

  useEffect(() => {
    if (subgroupClasses?.length && !values.gid) setFieldValue('gid', subgroupClasses[0].gid)
  }, [subgroupClasses])

  const openModal = (data) => () => {
    setSelectedItem(data || {})
  }

  const closeModal = () => setSelectedItem(null)

  const acceptConfirmation = () => {
    dispatch(deleteSubgroupNew(confirmation.id, buildRequestParams(values)));
    setConfirmation(false);
  };
  const rejectConfirmation = () => {
    setConfirmation(false);
  };

  const renderCell = (value) => value || <span className={styles.dash}>—</span>

  const renderLi = (data) => {
    const { course_title, name, teacher, track_profile_name, shortname, students_count, gid } = data
    return (
      <tr key={gid} onClick={openModal(data)}>
        <td className={styles.colName}>{renderCell(name)}</td>
        <td>{renderCell(shortname)}</td>
        <td>{renderCell(course_title)}</td>
        <td>{renderCell(track_profile_name)}</td>
        <td>{students_count ?? 0}</td>
        <td>{renderCell(teacher)}</td>
        <td className={styles.colAct}>
          <button
            type='button'
            className={styles.delIcon}
            title='Удалить'
            onClick={(e) => { e.stopPropagation(); setConfirmation({ id: gid, name }) }}
          >
            <BiTrash />
          </button>
        </td>
      </tr>
    )
  }

  const error = (fieldName) => (touched[fieldName] || submitCount) && errors[fieldName]

  return (
    <div className={styles.wrapper}>
      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить подгруппу ${confirmation.name}?`}
          acceptConfirmation={() => acceptConfirmation(confirmation?.name)}
          rejectConfirmation={rejectConfirmation}
        />
      )}
      {selectedItem &&
        <Modal
          closeModal={closeModal}
          data={selectedItem}
          height={'430px'}
          electiveCourse={electiveCourse}
          addMode={!selectedItem.gid}
          filters={values}
        />
      }
      <h1>Подгруппы</h1>

      <div className={styles.line}>
        {!electiveCourse &&
          <CustomSelect
            title="Учебный год"
            valuesName="xp_key"
            value={values.xp_key}
            options={schoolYears && arrayForSelect(schoolYears, 'name', 'xp_key')}
            error={error('xp_key')}
            setFieldValue={(name, value) => {
              setFieldValue(name, value)
              setFieldValue('cid', '')
              setFieldValue('gid', '')
            }}
            withoutAll
          />
        }
        <CustomSelect
          title="Предмет"
          valuesName="cid"
          value={values.cid}
          options={subgroupCourses && arrayForSelect(subgroupCourses, 'title', 'cid')}
          error={error('cid')}
          setFieldValue={(name, value) => {
            setFieldValue(name, value)
            setFieldValue('gid', '')
          }}
          withoutAll
        />
        {!electiveCourse &&
          <CustomSelect
            title="Класс"
            valuesName="gid"
            value={values.gid}
            options={subgroupClasses && arrayForSelect(subgroupClasses, 'grpName', 'gid')}
            error={error('gid')}
            setFieldValue={setFieldValue}
            withoutAll
            disabled={subgroupClasses?.length === 0}
            hint={subgroupClasses?.length === 0 ? 'Для выбранного предмета классы не найдены' : undefined}
          />
        }

        <div className={styles.checkbox}>
          <Checkbox
            checked={electiveCourse}
            onChange={() => setElectiveCourse(v => !v)}
            label="Элективный курс"
          />
          <Checkbox
            checked={statusClose}
            onChange={() => setStatusClose(v => !v)}
            label="Показать предметы со статусом 'Закрыт'"
          />
        </div>
        <Button
          className={styles.refreshBtn}
          onClick={handleSubmit}
          disabled={!electiveCourse && subgroupClasses?.length === 0}
        >
          <IoIosRefresh size={22} />
          Обновить
        </Button>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', textAlign: 'left' }}>Название</th>
                <th>Короткое название</th>
                <th onClick={() => handleSort('course_title')} style={{ cursor: 'pointer' }}>Предмет</th>
                <th>Профиль обучения</th>
                <th onClick={() => handleSort('students_count')} style={{ cursor: 'pointer' }}>Количество слушателей</th>
                <th>Преподаватель</th>
                <th>Удалить</th>
              </tr>
            </thead>
            <tbody>
              {sortedList.length
                ? sortedList.map(renderLi)
                : (
                  <tr className={styles.emptyRow}>
                    <td colSpan={7}>
                      {tableList
                        ? 'Нет подгрупп по выбранным фильтрам'
                        : 'Выберите фильтры и нажмите «Обновить»'}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.bottomBar}>
        <Button onClick={openModal()} disabled={!values.cid}>
          <IoIosAdd size={22} />
          Добавить
        </Button>
        <div className={styles.counter}>Всего записей: {sortedList.length}</div>
      </div>
    </div>
  )
}
